<!-- src/apps/secret/components/conceal/landing/HeroPanel.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';

  import TypingLine from './TypingLine.vue';

  const { t } = useI18n();

  // Honest capability stats — the mock's invented traffic/latency figures were
  // replaced with claims the backend actually delivers.
  const stats = [
    { value: 'AES-256', labelKey: 'landing.hero.stat_cipher' },
    { value: '1×', labelKey: 'landing.hero.stat_single_read' },
    { value: 'TTL', labelKey: 'landing.hero.stat_expiry' },
  ];
</script>

<template>
  <div class="relative flex flex-col justify-center overflow-hidden">
    <!-- fading grid backdrop -->
    <div
      class="pointer-events-none absolute inset-0
        bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)]
        [mask-image:radial-gradient(ellipse_at_30%_50%,black_20%,transparent_80%)]
        bg-[size:48px_48px]
        dark:bg-[linear-gradient(rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.07)_1px,transparent_1px)]"
      aria-hidden="true"></div>

    <div class="relative z-10">
      <div class="mb-9 flex items-center gap-2.5">
        <span
          class="inline-block size-2 rounded-full bg-brand-500 shadow-[0_0_10px_var(--color-brand-500)]"
          aria-hidden="true"></span>
        <span
          class="font-mono text-[11px] font-semibold tracking-[0.16em] text-brand-600 uppercase dark:text-brand-400">
          {{ t('landing.hero.badge') }}
        </span>
      </div>

      <h1
        class="mb-6 font-mono text-4xl leading-[1.05] font-bold tracking-[-0.035em] text-day-ink lg:text-[clamp(40px,5vw,68px)] dark:text-night-ink">
        {{ t('landing.hero.line1') }}
        <span class="text-brand-500">{{ t('landing.hero.line2') }}</span>
      </h1>

      <!-- Tagline: the ephemeral promise, as one confident line — not a faded
           italic block competing with the headline. -->
      <p
        class="mb-6 flex items-start gap-2.5 font-mono text-base leading-snug text-day-ink-muted lg:text-lg dark:text-night-ink-muted">
        <span
          class="mt-2 h-px w-6 shrink-0 bg-brand-500 lg:mt-3"
          aria-hidden="true"></span>
        <span>{{ t('landing.hero.line3') }}</span>
      </p>

      <p
        class="mb-11 max-w-md text-[15px] leading-[1.7] text-day-ink-muted dark:text-night-ink-muted">
        {{ t('landing.hero.subtitle') }}
      </p>

      <TypingLine />

      <div
        class="mt-11 flex border-t border-day-line pt-8 dark:border-white/[0.07]"
        role="list">
        <div
          v-for="(stat, i) in stats"
          :key="stat.labelKey"
          role="listitem"
          class="pr-7"
          :class="i < stats.length - 1 ? 'mr-7 border-r border-day-line dark:border-white/[0.07]' : ''">
          <div
            class="font-mono text-xl font-bold tracking-[-0.02em] text-day-ink lg:text-2xl dark:text-night-ink">
            {{ stat.value }}
          </div>
          <div
            class="mt-1 font-mono text-[9px] tracking-[0.08em] text-day-ink-faint uppercase dark:text-night-ink-faint">
            {{ t(stat.labelKey) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
